import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AgentType } from '../models/agent-config.model';
import { type RootState } from '@/platform/store/types';
import {
  type AgentGenerationErrorCode,
  type AgentGenerationStatus,
  type AgentGenerationStatusEvent,
} from '../models/agent-generation.model';
import type { WithSlice } from '@reduxjs/toolkit';

export interface AgentGenerationState {
  jobId: string | null;
  agentConfigId: string | null;
  status: AgentGenerationStatus;
  currentNode: string | null;
  completed: number;
  total: number;
  errorCode: AgentGenerationErrorCode | null;
  /** How many times the user has manually retried this agent's generation. */
  retryCount: number;
  /** Cached for the retry button — same payload, second attempt. */
  lastSourceIds: string[];
  lastDisplayName: string;
  lastAgentType: AgentType | null;
}

const initialState: AgentGenerationState = {
  jobId: null,
  agentConfigId: null,
  status: 'idle',
  currentNode: null,
  completed: 0,
  total: 0,
  errorCode: null,
  retryCount: 0,
  lastSourceIds: [],
  lastDisplayName: '',
  lastAgentType: null,
};

export const agentGenerationSlice = createSlice({
  name: 'agentGeneration',
  initialState,
  reducers: {
    /** Called right after the POST succeeds. Resets status to `queued`. If the
     *  agentConfigId differs from the previous run, retryCount resets to 0. */
    generationStarted: (
      state,
      action: PayloadAction<{
        jobId: string;
        agentConfigId: string;
        sourceIds: string[];
        displayName: string;
        agentType: AgentType;
        isRetry: boolean;
      }>,
    ) => {
      const sameAgent = state.agentConfigId === action.payload.agentConfigId;
      state.jobId = action.payload.jobId;
      state.agentConfigId = action.payload.agentConfigId;
      state.status = 'queued';
      state.currentNode = null;
      state.completed = 0;
      state.total = 0;
      state.errorCode = null;
      state.lastSourceIds = action.payload.sourceIds;
      state.lastDisplayName = action.payload.displayName;
      state.lastAgentType = action.payload.agentType;
      state.retryCount =
        sameAgent && action.payload.isRetry ? state.retryCount + 1 : 0;
    },

    /** Apply a socket status event. Ignored if it doesn't match the current
     *  jobId — guards against stale events from a previous run. */
    generationProgress: (
      state,
      action: PayloadAction<AgentGenerationStatusEvent>,
    ) => {
      if (action.payload.job_id !== state.jobId) return;
      state.status = action.payload.status;
      state.completed = action.payload.completed;
      state.total = action.payload.total;
      state.currentNode = action.payload.current_node ?? null;
      if (action.payload.status === 'failed') {
        state.errorCode = action.payload.error_code ?? 'UNKNOWN';
      } else {
        state.errorCode = null;
      }
    },

    /** Reset everything (e.g. when the user navigates away from the wizard). */
    generationReset: () => initialState,
  },
});

export const { generationStarted, generationProgress, generationReset } =
  agentGenerationSlice.actions;

const selectSlice = (state: RootState): AgentGenerationState =>
  state.agentGeneration ?? initialState;

export const selectGenerationState = (state: RootState) => selectSlice(state);
export const selectGenerationStatus = (state: RootState) =>
  selectSlice(state).status;
export const selectGenerationProgressPct = (state: RootState) => {
  const { completed, total } = selectSlice(state);
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
};
export const selectGenerationCurrentNode = (state: RootState) =>
  selectSlice(state).currentNode;
export const selectGenerationRetryCount = (state: RootState) =>
  selectSlice(state).retryCount;
export const selectGenerationErrorCode = (state: RootState) =>
  selectSlice(state).errorCode;
export const selectGenerationAgentConfigId = (state: RootState) =>
  selectSlice(state).agentConfigId;
export const selectGenerationLastPayload = (state: RootState) => {
  const s = selectSlice(state);
  return s.lastAgentType
    ? {
        sourceIds: s.lastSourceIds,
        displayName: s.lastDisplayName,
        agentType: s.lastAgentType,
      }
    : null;
};

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof agentGenerationSlice> {}
}
